import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useId } from "react";

export type DSCheckboxSize = "sm" | "md";

export interface DSCheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> {
  size?: DSCheckboxSize;
  checked?: boolean;
  /** Accessible label for screen readers (required if no visible label) */
  "aria-label"?: string;
  dataTestId: string;
  ref?: React.Ref<HTMLInputElement>;
}

export const DSCheckbox = ({
  size = "sm",
  checked = false,
  disabled = false,
  "aria-label": ariaLabel,
  dataTestId,
  id: providedId,
  ref,
  ...props
}: Omit<DSCheckboxProps, "className">) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  // Base checkbox container styles
  const baseStyles =
    "relative inline-flex items-center justify-center rounded-minimal border-2 transition-all";

  // Size styles
  const sizeStyles = {
    sm: "size-5",
    md: "size-7",
  };

  // Icon size styles
  const iconSizeStyles = {
    sm: "text-xs",
    md: "text-lg",
  };

  // State styles
  const stateStyles = disabled
    ? checked
      ? "bg-disabled border-disabled opacity-50 cursor-not-allowed"
      : "bg-input border-disabled opacity-50 cursor-not-allowed"
    : checked
      ? "bg-brand border-brand shadow-[inset_0px_0px_9.5px_0px_var(--surface-brand-hover)] hover:bg-[var(--surface-brand-hover)]"
      : "bg-input border-brand hover:border-[var(--border-brand-hover)]";

  return (
    <label
      htmlFor={id}
      className={`inline-flex items-center ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-intercom-target={dataTestId}
        className="sr-only peer"
        {...props}
      />
      <div
        className={`${baseStyles} ${sizeStyles[size]} ${stateStyles}`}
        aria-hidden="true"
      >
        {checked && (
          <FontAwesomeIcon
            icon={faCheck}
            className={`text-white ${iconSizeStyles[size]}`}
          />
        )}
      </div>
    </label>
  );
};

DSCheckbox.displayName = "DSCheckbox";
